// 전환 속도·곡선은 이 카드에만 건다(전역 index.css 가 아니라).
import '../styles/docsPlaylist.css';

/** 유튜브 기본 썸네일(320×180). 크기를 알고 있으니 자리를 미리 잡는다. */
const THUMB_WIDTH = 320;
const THUMB_HEIGHT = 180;

interface DocsPlaylistCardProps {
  title: string;
  description: string;
  /** 유튜브 재생목록 주소(`…/playlist?list=<id>`). 플레이어도 여기서 list id 를 읽는다. */
  href: string;
  /** 썸네일이자 처음 재생할 영상 — 재생목록의 첫 영상. */
  thumbnailVideoId: string;
  /** 지금 이 카드가 재생 중인지. 한 번에 하나만 재생하도록 부모가 정한다. */
  playing: boolean;
  /** 플레이어 옆 좁은 칸에 놓일 때 — 제목·설명을 조금 작게. */
  compact?: boolean;
  onPlay: () => void;
  onClose: () => void;
}

/**
 * 재생목록 카드. 썸네일을 누르면 플레이어가 된다(재생 배치는 부모가 정한다).
 *
 * 누르기 전까지는 썸네일 이미지만 둔다 — iframe 을 미리 박으면 카드 하나당 ~1MB JS 와
 * 서드파티 쿠키가 붙는다(본문 `<YouTube>` 블록과 같은 이유). 도메인도 nocookie 를 쓴다.
 *
 * YouTube 는 재생목록 썸네일을 API 키 없이 주지 않아서 첫 영상 썸네일을 대신 쓴다.
 * 재생은 `embed/<첫 영상>?list=<재생목록>` — 첫 영상부터 틀고, 플레이어 안에서
 * 재생목록의 다음 영상으로 넘어간다. 재생목록 첫 영상을 바꾸면 여기 id 도 바꾼다.
 *
 * `viewTransitionName` 은 카드마다 고유하다 — 배치가 바뀔 때 브라우저가 같은 이름의
 * 카드를 옛 자리에서 새 자리로 이어 움직인다(썸네일이 플레이어 자리로 커지는 애니메이션).
 * 속도·곡선은 `styles/docsPlaylist.css` 가 `docs-playlist-card` 에만 건다.
 */
export function DocsPlaylistCard({
  title,
  description,
  href,
  thumbnailVideoId,
  playing,
  compact = false,
  onPlay,
  onClose,
}: DocsPlaylistCardProps) {
  const listId = new URL(href).searchParams.get('list') ?? '';

  return (
    <div
      className="docs-playlist-card"
      style={{ viewTransitionName: `docs-playlist-${thumbnailVideoId}` }}
    >
      <div
        className={[
          'relative overflow-hidden rounded-xl border bg-neutral-900 transition-shadow',
          playing
            ? 'border-transparent shadow-xl'
            : 'border-neutral-200 dark:border-white/12',
        ].join(' ')}
      >
        {playing ? (
          <iframe
            className="block aspect-video w-full"
            src={`https://www.youtube-nocookie.com/embed/${thumbnailVideoId}?list=${listId}&autoplay=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={onPlay}
            aria-label={`${title} 재생`}
            className="group block w-full"
          >
            <img
              src={`https://i.ytimg.com/vi/${thumbnailVideoId}/mqdefault.jpg`}
              alt=""
              width={THUMB_WIDTH}
              height={THUMB_HEIGHT}
              loading="lazy"
              decoding="async"
              className="block aspect-video h-auto w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            />
            <span
              aria-hidden
              className={[
                'absolute top-1/2 left-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center bg-[#f03] text-white opacity-90 transition group-hover:scale-110 group-hover:opacity-100',
                compact
                  ? 'h-8 w-11 rounded-lg text-[12px]'
                  : 'h-10 w-14 rounded-xl text-[14px]',
              ].join(' ')}
            >
              ▶
            </span>
          </button>
        )}
      </div>

      <div
        className={[
          'flex items-start justify-between gap-3',
          compact ? 'mt-2' : 'mt-3',
        ].join(' ')}
      >
        <div className="min-w-0">
          <p
            className={[
              'font-bold text-neutral-900 dark:text-neutral-100',
              compact ? 'text-[14px]' : 'text-[15px]',
            ].join(' ')}
          >
            {title}
          </p>
          <p
            className={[
              'mt-0.5 truncate text-neutral-500 dark:text-neutral-400',
              compact ? 'text-[12.5px]' : 'text-[13px]',
            ].join(' ')}
          >
            {description}
          </p>
        </div>
        {playing ? (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-[12.5px] font-semibold text-neutral-500 hover:text-brand-600 dark:text-neutral-400"
          >
            닫기
          </button>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="shrink-0 text-[12.5px] font-semibold text-neutral-500 hover:text-brand-600 dark:text-neutral-400"
          >
            유튜브 ↗
          </a>
        )}
      </div>
    </div>
  );
}
